import type { Uuid } from "@/domains/global/identifiers";
import type { RecallPermissionMap, RecallSdkState } from "@/modules/electron/types";
import type { MediaRecorderAudioChunk } from "@/store/audio/recording/audio-recording.types";

export type EnsureLocalRecordingStartedArgs = {
  audioRecordingId: Uuid;
};

export type GetInitialAudioChunkCountArgs = {
  audioRecordingId: Uuid;
};

export type AppendAudioChunkArgs = {
  audioRecordingId: Uuid;
  chunk: MediaRecorderAudioChunk;
};

export type CompleteRecordingArgs = {
  audioRecordingId: Uuid;
};

export type GetPersistedDurationMsArgs = {
  audioRecordingId: Uuid;
};

export type GetArtifactDebugSummaryArgs = {
  audioRecordingId: Uuid;
};

export type GetUploadableArtifactStatusArgs = {
  audioRecordingId: Uuid;
};

export type RemoveLocalArtifactsArgs = {
  audioRecordingId: Uuid;
};

export enum AudioRecordingArtifactState {
  Writing = "writing",
  Finalized = "finalized",
}

export type AudioRecordingArtifactDebugSummary = {
  durationMs: number;
  partCount: number;
  sizeBytes: number;
  state?: AudioRecordingArtifactState;
  filePath?: string;
  appendCount?: number;
  bytesWritten?: number;
  createdAt?: string;
  updatedAt?: string;
  firstAppendAt?: string;
  lastAppendAt?: string;
  finalizedAt?: string;
  recallSdkStateAtStart?: RecallSdkState;
  recallPermissionsAtStart?: RecallPermissionMap;
};

export type UploadableAudioRecordingArtifactStatus = {
  artifactSizeBytes: number | null;
  hasUploadableArtifact: boolean;
};

export interface AudioRecordingStrategyDriver {
  ensureLocalRecordingStarted(args: EnsureLocalRecordingStartedArgs): Promise<void>;
  getInitialAudioChunkCount(args: GetInitialAudioChunkCountArgs): Promise<number>;
  appendAudioChunk(args: AppendAudioChunkArgs): Promise<void>;
  completeRecording(args: CompleteRecordingArgs): Promise<number>;
  getPersistedDurationMs(args: GetPersistedDurationMsArgs): Promise<number | null>;
  getArtifactDebugSummary(
    args: GetArtifactDebugSummaryArgs
  ): Promise<AudioRecordingArtifactDebugSummary>;
  getUploadableArtifactStatus(
    args: GetUploadableArtifactStatusArgs
  ): Promise<UploadableAudioRecordingArtifactStatus>;
  removeLocalArtifacts(args: RemoveLocalArtifactsArgs): Promise<void>;
}
